import { useEffect, useCallback } from 'react';
import { Box, Container, Grid, Link, Typography } from '@mui/material';
import { CoreGlobalFooter } from '@care/navigation';
import Head from 'next/head';
import { useActiveTheme } from '@care/react-component-lib';
import {
  AmplitudeEvent,
  setDeviceDataFromHeaders,
  trackAmplitudeEvent,
} from '@/lib/analyticsHelper';
import { SeoGlobalNavbar } from '@/components/common/SeoGlobalNavbar';
import Breadcrumbs from '@/components/Breadcrumbs';
import { seoCitiesBySegmentAndState_seoCitiesBySegmentAndState_SEOCitiesBySegmentAndStateSuccess_linkList as SEOSegmentationLink } from '@/__generated__/seoCitiesBySegmentAndState';
import { useResponsive } from '@/components/hooks/useResponsive';
import { CitiesBySegmentAndStateDirectoryProps } from './citiesBySegmentAndStateDirectoryProps';

/**
 * CitiesBySegmentAndStateDirectory component, also known as our "State Directory Page", is for rendering
 * our state directory pages such as /explore/d/activities/tx or /child-care/tx in the future.
 * It renders a list of cities that provide a specific service in the given state.
 */

const CitiesBySegmentAndStateDirectory = ({
  pageData,
  seoSegmentId,
  deviceChar,
}: CitiesBySegmentAndStateDirectoryProps) => {
  const { isEmbraceTheme } = useActiveTheme();
  const {
    seoCitiesBySegmentAndState: { title, linkList },
    breadcrumbs,
    activityDetails,
  } = pageData;

  // Check if it's from the explore path
  const isExplorePath = seoSegmentId?.includes('/explore/d/');

  const { isSmallViewport } = useResponsive();
  const { segmentNamePluralLowerCase } = activityDetails ?? {};
  const stringifyDeviceChar = JSON.stringify(deviceChar);
  const segmentName = breadcrumbs[0]?.content.toLowerCase();
  const stateName = breadcrumbs[2]?.content;
  const numOfColumns = isSmallViewport ? 1 : 3;

  const metaTitle = isExplorePath ? `Browse ${title} by City` : `${title} - Care.com`;

  const metaDescription = isExplorePath
    ? `Find ${segmentNamePluralLowerCase} by city in ${stateName} and book an activity today`
    : `Find ${segmentName} in ${stateName} by city.`;

  const trackPageViewedEvent = useCallback(() => {
    const amplitudeDataForPageViewed = {
      screen_name: 'SEO Directory Page',
      seo_page_title: title,
      seo_page_type: 'SEO Directory page',
      page_template: 'State Directory',
      seo_page_id: 1443,
    };

    trackAmplitudeEvent(AmplitudeEvent.seoPageViewed, amplitudeDataForPageViewed);
  }, [title]);

  useEffect(() => {
    trackPageViewedEvent();
  }, [trackPageViewedEvent]);

  useEffect(() => {
    if (deviceChar && Object.keys(deviceChar).length > 0) {
      setDeviceDataFromHeaders({
        device_os: deviceChar?.device_os,
        is_mobile: deviceChar?.is_mobile,
        mobile_browser: deviceChar?.mobile_browser,
        mobile_browser_version: deviceChar?.mobile_browser_version,
        ...deviceChar,
      });
    }
  }, [stringifyDeviceChar, deviceChar]);

  const citiesList = (cities: SEOSegmentationLink[]): JSX.Element[] => {
    return cities.map(({ content, url }) => {
      return (
        <Box key={content} component="li" mt={1.5} sx={{ listStyleType: 'none' }}>
          <Link
            variant="link1"
            href={url}
            sx={{
              ':hover': {
                textDecoration: 'none',
              },
            }}>
            {content}
          </Link>
        </Box>
      );
    });
  };

  const renderCityList = (list: SEOSegmentationLink[], columns: number) => {
    const sortedList = [...list].sort((a, b) => a.content.localeCompare(b.content));
    const numOfCitiesPerColumn = Math.ceil(sortedList.length / columns);
    const cityList = Array.from({ length: columns }, (_, index) => {
      const start = index * numOfCitiesPerColumn;
      const end = start + numOfCitiesPerColumn;
      return sortedList.slice(start, end);
    });

    return cityList.map((cities, index) => {
      return (
        // eslint-disable-next-line react/no-array-index-key
        <Grid item xs={12} sm={6} md={4} key={index + 1}>
          {citiesList(cities)}
        </Grid>
      );
    });
  };

  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        <meta name="description" content={`${metaDescription}`} />
      </Head>
      <SeoGlobalNavbar />
      <Container maxWidth="lg" sx={{ px: 3 }}>
        <Grid container>
          <Grid item xs={12}>
            <Box mt={5}>{breadcrumbs.length > 0 && <Breadcrumbs breadcrumbs={breadcrumbs} />}</Box>
          </Grid>
          {title && (
            <Grid item xs={12} pb={3}>
              <Typography variant="h2" component="h1" mt={3}>
                {title}
              </Typography>
            </Grid>
          )}
          <Grid container item xs={12} mb={15}>
            {renderCityList(linkList, numOfColumns)}
          </Grid>
        </Grid>
      </Container>
      <CoreGlobalFooter variant={isEmbraceTheme ? 'inverse' : 'default'} />
    </>
  );
};

export default CitiesBySegmentAndStateDirectory;
